// Traslados entre bodegas. Port de views/traslados.js.
import { useState, useEffect, useMemo } from 'react';
import { useBodegas } from '../hooks/useBodegas';
import { idBodegaPrincipal } from '../lib/api/bodegas';
import { useProductos } from '../hooks/useProductos';
import { usePresentaciones } from '../hooks/usePresentaciones';
import { useTrasladar } from '../hooks/useTraslados';
import { Field } from '../components/Field';
import { SkeletonTable } from '../components/Skeletons';
import { ErrorBox } from '../components/ErrorBox';
import { useToast } from '../components/Toast';
import type { ItemTipo } from '../types';

export function Traslados() {
  const { data: bodegas, isLoading, isError, error } = useBodegas();
  const { data: productos } = useProductos();
  const { data: presentaciones } = usePresentaciones();
  const trasladar = useTrasladar();
  const toast = useToast();

  const items = useMemo(() => [
    ...(productos ?? []).map((p) => ({ value: 'producto:' + p.id, label: '🏷️ ' + p.nombre })),
    ...(presentaciones ?? []).map((p) => ({ value: 'presentacion:' + p.id, label: '📦 ' + p.nombre })),
  ], [productos, presentaciones]);

  const [origenId, setOrigenId] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [item, setItem] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [enviado, setEnviado] = useState(false);

  // Preselección: origen = bodega principal, destino = otra distinta.
  useEffect(() => {
    if (bodegas && bodegas.length >= 2) {
      const principal = idBodegaPrincipal(bodegas);
      setOrigenId((o) => o || principal);
      setDestinoId((d) => d || (bodegas.find((b) => b.id !== principal)?.id ?? bodegas[1].id));
    }
  }, [bodegas]);
  useEffect(() => { if (!item && items.length) setItem(items[0].value); }, [items, item]);

  const registrar = async () => {
    setEnviado(true);
    const cant = Number(cantidad);
    if (!origenId || !destinoId || !item || !cant || cant <= 0) return;
    if (origenId === destinoId) { toast.warning('Bodegas iguales', 'Elige un origen y un destino distintos.'); return; }
    const [tipo, refId] = item.split(':') as [ItemTipo, string];
    try {
      await trasladar.mutateAsync({ origenId, destinoId, tipo, refId, cantidad: cant, observaciones });
      toast.success('Traslado registrado', 'Las existencias se movieron correctamente.');
      setCantidad(''); setObservaciones(''); setEnviado(false);
    } catch (e) {
      toast.error('No se pudo trasladar', (e as Error).message);
    }
  };

  if (isLoading) {
    return (<><div className="page-head"><h2>Traslados</h2></div><SkeletonTable rows={4} /></>);
  }
  if (isError) {
    return (<><div className="page-head"><h2>Traslados</h2></div><ErrorBox mensaje={(error as Error).message} /></>);
  }
  if (!bodegas || bodegas.length < 2) {
    return (
      <>
        <div className="page-head"><h2>Traslados</h2></div>
        <div className="card"><div className="empty-state"><i className="fa-solid fa-right-left" />Necesitas al menos dos bodegas para trasladar existencias.</div></div>
      </>
    );
  }

  return (
    <>
      <div className="page-head"><h2>Traslados entre bodegas</h2></div>
      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-head"><span className="chip c-primary"><i className="fa-solid fa-right-left" /></span><h3>Nuevo traslado</h3></div>
        <div className="field-row">
          <Field name="origenId" label="Origen" type="select" required value={origenId} onChange={setOrigenId}
            options={bodegas.map((b) => ({ value: b.id, label: b.nombre }))} invalid={enviado && !origenId} />
          <Field name="destinoId" label="Destino" type="select" required value={destinoId} onChange={setDestinoId}
            options={bodegas.map((b) => ({ value: b.id, label: b.nombre }))} invalid={enviado && !destinoId} />
        </div>
        <Field name="item" label="Ítem a trasladar" type="select" required value={item} onChange={setItem}
          options={items} invalid={enviado && !item} />
        <Field name="cantidad" label="Cantidad" type="number" required min={1} value={cantidad} onChange={setCantidad}
          invalid={enviado && (!cantidad || Number(cantidad) <= 0)} />
        <Field name="observaciones" label="Observaciones" type="textarea" value={observaciones} onChange={setObservaciones} />
        <button type="button" className={`btn primary${trasladar.isPending ? ' loading' : ''}`} style={{ width: '100%' }} onClick={registrar}>
          <i className="fa-solid fa-paper-plane" /> Registrar traslado
        </button>
      </div>
    </>
  );
}
